/**
 * Pure hook that scrolls the message virtual list to a target message,
 * loading any missing older pages first, then flashing a highlight ring.
 *
 * The adapter (use-chat-scroll-to-message.hook.ts) wires it to Redux + RTK.
 */

import { logger } from '@saaga/shared/utils/logger.util'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'

const HIGHLIGHT_DURATION_MS = 1500
const MAX_JUMP_RETRIES = 1

export interface ScrollTarget {
  id: string
  pageNumber: number
  pageSize: number
}

export interface FreshPin {
  itemId: string
  pageNumber: number
  pageSize: number
}

export type JumpErrorReason = 'aborted' | 'not-found' | 'unknown'

export interface FetchPageArg {
  page: number
  pageSize: number
}

export interface UseScrollToMessageParams {
  virtuosoRef: RefObject<VirtuosoHandle | null>
  firstItemIndex: number
  messageList: { id: string }[]
  currentPage: number
  messageExistsInStore: (id: string) => boolean
  fetchPage: (arg: FetchPageArg, ctx: { signal: AbortSignal }) => Promise<void>
  refetchPins: () => Promise<FreshPin[]>
  onError?: (reason: JumpErrorReason, error?: unknown) => void
}

export interface UseScrollToMessageReturn {
  scrollToMessage: (target: ScrollTarget) => Promise<void>
  highlightedMessageId: string | null
  isJumping: boolean
  jumpingTargetId: string | null
}

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError'

interface JumpContext {
  params: UseScrollToMessageParams
  abortRef: RefObject<AbortController | null>
  setJumpingTargetId: (id: string | null) => void
  finalizeScroll: (id: string) => void
}

const fetchMissingPages = async (
  target: ScrollTarget,
  currentPage: number,
  fetchPage: UseScrollToMessageParams['fetchPage'],
  signal: AbortSignal
): Promise<void> => {
  for (let page = currentPage + 1; page <= target.pageNumber; page++) {
    if (signal.aborted) return
    await fetchPage({ page, pageSize: target.pageSize }, { signal })
  }
}

const retryWithFreshPin = async (
  target: ScrollTarget,
  refetchPins: UseScrollToMessageParams['refetchPins']
): Promise<ScrollTarget | null> => {
  const freshPins = await refetchPins()
  const fresh = freshPins.find((pin) => pin.itemId === target.id)
  if (!fresh) return null
  return { id: fresh.itemId, pageNumber: fresh.pageNumber, pageSize: fresh.pageSize }
}

const handleJumpError = (err: unknown, ctx: JumpContext): void => {
  if (isAbortError(err)) {
    ctx.params.onError?.('aborted')
    return
  }
  logger.error('scroll-to-message: fetch failed', err)
  ctx.params.onError?.('unknown', err)
}

const releaseJump = (ctx: JumpContext, controller: AbortController): void => {
  if (ctx.abortRef.current === controller) {
    ctx.abortRef.current = null
    ctx.setJumpingTargetId(null)
  }
}

const tryRetryOrFail = async (
  target: ScrollTarget,
  attempt: number,
  ctx: JumpContext
): Promise<void> => {
  if (attempt >= MAX_JUMP_RETRIES) {
    ctx.params.onError?.('not-found')
    return
  }
  const freshTarget = await retryWithFreshPin(target, ctx.params.refetchPins)
  if (!freshTarget) {
    ctx.params.onError?.('not-found')
    return
  }
  await runJump(freshTarget, attempt + 1, ctx)
}

const runJump = async (target: ScrollTarget, attempt: number, ctx: JumpContext): Promise<void> => {
  if (ctx.params.messageExistsInStore(target.id)) {
    ctx.finalizeScroll(target.id)
    return
  }
  const controller = new AbortController()
  ctx.abortRef.current = controller
  ctx.setJumpingTargetId(target.id)
  try {
    await fetchMissingPages(target, ctx.params.currentPage, ctx.params.fetchPage, controller.signal)
    if (controller.signal.aborted) return
    if (ctx.params.messageExistsInStore(target.id)) {
      ctx.finalizeScroll(target.id)
      return
    }
    await tryRetryOrFail(target, attempt, ctx)
  } catch (err) {
    handleJumpError(err, ctx)
  } finally {
    releaseJump(ctx, controller)
  }
}

interface ScrollStateRefs {
  messageListRef: RefObject<{ id: string }[]>
  firstItemIndexRef: RefObject<number>
  highlightTimerRef: RefObject<ReturnType<typeof setTimeout> | null>
  abortRef: RefObject<AbortController | null>
}

const useLatestRefs = (params: UseScrollToMessageParams): ScrollStateRefs => {
  const messageListRef = useRef(params.messageList)
  const firstItemIndexRef = useRef(params.firstItemIndex)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    messageListRef.current = params.messageList
  }, [params.messageList])
  useEffect(() => {
    firstItemIndexRef.current = params.firstItemIndex
  }, [params.firstItemIndex])
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
      abortRef.current?.abort()
    },
    []
  )

  return { messageListRef, firstItemIndexRef, highlightTimerRef, abortRef }
}

interface FinalizeScrollDeps {
  refs: ScrollStateRefs
  params: UseScrollToMessageParams
  setHighlightedMessageId: (id: string | null) => void
}

const createFinalizeScroll =
  (deps: FinalizeScrollDeps) =>
  (id: string): void => {
    const arrayIndex = deps.refs.messageListRef.current.findIndex((m) => m.id === id)
    if (arrayIndex < 0) {
      logger.warn('scroll-to-message: target not in list after fetch', id)
      deps.params.onError?.('not-found')
      return
    }
    deps.params.virtuosoRef.current?.scrollToIndex({
      index: deps.refs.firstItemIndexRef.current + arrayIndex,
      align: 'center',
      behavior: 'smooth',
    })
    if (deps.refs.highlightTimerRef.current) clearTimeout(deps.refs.highlightTimerRef.current)
    deps.setHighlightedMessageId(id)
    deps.refs.highlightTimerRef.current = setTimeout(() => {
      deps.setHighlightedMessageId(null)
    }, HIGHLIGHT_DURATION_MS)
  }

export const useScrollToMessage = (params: UseScrollToMessageParams): UseScrollToMessageReturn => {
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)
  const [jumpingTargetId, setJumpingTargetId] = useState<string | null>(null)
  const refs = useLatestRefs(params)

  const finalizeScroll = useCallback(
    createFinalizeScroll({ refs, params, setHighlightedMessageId }),
    [refs, params]
  )

  const scrollToMessage = useCallback(
    async (target: ScrollTarget): Promise<void> => {
      refs.abortRef.current?.abort()
      await runJump(target, 0, {
        params,
        abortRef: refs.abortRef,
        setJumpingTargetId,
        finalizeScroll,
      })
    },
    [params, finalizeScroll, refs.abortRef]
  )

  return {
    scrollToMessage,
    highlightedMessageId,
    isJumping: jumpingTargetId !== null,
    jumpingTargetId,
  }
}
